import React, { useState } from 'react';
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getCoverageTypes } from '../../actions/premiumSettingsWizard'
import { getCaseTypes } from '../../actions/caseTypes'
import { stringToNumber, roundToFourDecimals } from '../../helpers/functions'
import { stagedPremiumSettingsShowTableFields } from '../../helpers/applicationPremiumSettingsFunctions'
import { requestPatchApplicationStagedPremiumSettings } from '../../reducers/stagedPremiumsWizard'
import { CANADA_COUNTRY, COUNTRY, FLAT_PREMIUM_SETTINGS, LOADER, NONE, STAGED_PREMIUM_EDIT, STAGED_PREMIUM_SETTINGS } from '../../helpers/variables'
import { Loader } from 'arag-ui-lib'
import StagedPremiumSettingsWizardHOC from '../PremiumSettings/stagedPremiumSettingsWizardHOC'
import PremiumSettingsWizardHOC from '../PremiumSettings/premiumSettingsWizardHOC'
import ApplicationStagedPremiumSettings from '../../components/PremiumSettings/applicationStagedPremiumSettings'

const ApplicationPremiumSettingsHOC = (props) => {
    const {
        isChangingStatusOfApp,
        showTable,
        isEditMode,
        isApplicationPremium,
        applicationPremiumCancelHandler,
        selectedApplicationCaseTypeId,
        onApplicationPremiumEdit,
        applicationPremiumSaveButtonLabel,
        isEditStaged,
        onEditStaged,
        onCancel,
    } = props
    const dispatch = useDispatch()
    const applicationPolicy = useSelector((state) => state.applicationPolicy);
    const premiumSettingsWizard = useSelector((state) => state.premiumSettingsWizard);
    const stagedPremiumSettings = useSelector((state) => state.stagedPremiums);
    const stagedPremiumsWizard = useSelector((state) => state.stagedPremiumsWizard);
    const stagedSubSettingsWizard = Object.values(useSelector((state) => state.stagedPremiumsWizard.stagesSubSettings));
    const agentsList = Object.values(useSelector((state) => state.stagedPremiumsWizard.agentCommissionsList));
    const appStagedSubSettings = Object.values(useSelector((state) => state.stagedPremiums.stagesSubSettings));
    const applicationId = useSelector((state) => state.applicationsAndPolicies.selectedApplication.id);
    const loggedInUser = useSelector((state) => state.user.loggedInUser);
    const applicationPremiumSettingsRevisions = useSelector((state) => state.stagedPremiums.revisions);
    const [selectedPreviousUpdate, setSelectedPreviousUpdate] = useState(null);
    const [openRevisionsModal, setOpenRevisionsModal] = useState(false);

    // FormGroup emphasis map for the show view — fields that changed vs the most recent previous revision
    const appStagedEmphasizedFields = applicationPremiumSettingsRevisions?.length > 0
        ? stagedPremiumSettingsShowTableFields(
            stagedPremiumSettings.stagedPremiumSetting,
            applicationPremiumSettingsRevisions[0]
        )
        : null;

    // Position of the selected history item — used to find its predecessor
    const selectedRevisionIndex = selectedPreviousUpdate
        ? applicationPremiumSettingsRevisions?.findIndex(r => r.id === selectedPreviousUpdate.id) ?? -1
        : -1;

    // FormGroup highlight map for the history modal — fields that changed vs the revision before the selected one
    const appStagedHighlightedFields = (selectedPreviousUpdate && selectedRevisionIndex >= 0 && selectedRevisionIndex + 1 < applicationPremiumSettingsRevisions.length)
        ? stagedPremiumSettingsShowTableFields(
            selectedPreviousUpdate,
            applicationPremiumSettingsRevisions[selectedRevisionIndex + 1]
        )
        : null;

    const stagedPremiumWizardLoader = useSelector((state) => 
        state.stagedPremiumsWizard.fetching || 
        state.stagedPremiumsWizard.fetchingStages || 
        state.stagedPremiumsWizard.fetchingCoverageTypes ||
        state.stagedPremiumsWizard.changingApplicationStatus ||
        state.stagedPremiumsWizard.patching ||
        state.premiumSettingsWizard.fetchingStagedPremium
    );
    const premiumSettingsLoader = useSelector((state) => 
        state.premiumSettings.fetchingPremiumWithCombination ||
        state.premiumSettings.fetching ||
        state.premiumSettings.fetchingPremiumSettingById ||
        state.premiumSettings.patching
    );
    const goingLiveLoading = stagedPremiumWizardLoader || premiumSettingsLoader;
    const flatPremiumSettingsExist = premiumSettingsWizard.tableData.length > 0;
    const stagedPremiumSettingsExist = appStagedSubSettings.length > 0;
    const isCanadaCountry = COUNTRY === CANADA_COUNTRY;

    useEffect(() => {
        dispatch(getCoverageTypes())
        // The wizard HOC skips this fetch in show-table mode (its parent on the
        // global Premium Settings page guards on caseTypes.fetching, which would
        // remount the wizard and loop). Fetch it here so the application premium
        // settings flow can detect Clinical Negligence to show Recoverable rows.
        dispatch(getCaseTypes())
    }, []);
    
    const handleStagedSubmit = () => {
        const stagesSettingsBody = stagedSubSettingsWizard.map((s) => {
            const premiumAmount = stringToNumber(s.premiumAmount);
            return {
                ...s,
                // underwriterRate/commission below are the real, full-precision computed
                // amounts sent straight to the PATCH body for storage — nothing server-side
                // recalculates these for staged premiums, so this is the value that gets
                // persisted. Round only to 4dp to avoid raw floating-point noise; the UI
                // rounds to 2dp for display separately, at render time.
                underwriterRate: roundToFourDecimals(
                    stringToNumber(stagedPremiumsWizard.underwriterRatePercentage) * 0.01 * premiumAmount
                ),
                underwriterRatePercentage: stringToNumber(stagedPremiumsWizard.underwriterRatePercentage),
                stagedPremiumSubSettingCommissionDtos: agentsList.map((c) => ({
                    ...c,
                    lawsysId: c.id,
                    lawsysName: c.name,
                    commission: roundToFourDecimals(stringToNumber(c.commissionPercentage) * 0.01 * premiumAmount),
                    commissionPercentage: stringToNumber(c.commissionPercentage),
                })),
            };
        });
        const sendBody = {
            id: stagedPremiumsWizard.applicationPremiumId,
            notes: stagedPremiumsWizard.notes,
            comments: stagedPremiumsWizard.comments,
            limitOfIndemnity: 0,
            DeductibleOptionId: stagedPremiumsWizard.selectedDeductible,
            CoverageOptionId: stagedPremiumsWizard.selectedCoverageType,
            EndorsementOptionId: stagedPremiumsWizard.selectedEndorsement,
            averageExpectedPremium: stagedPremiumsWizard.averageExpectedPremium,
            applicationStagedPremiumSubSettingDtos: stagesSettingsBody,
        };
        const request = {
            sendBody,
            applicationId,
        };
        if (isApplicationPremium && isChangingStatusOfApp === true) {
            request.makeApplicationLive = true;
            request.sendEmail = applicationPolicy.sendEmailNotification;
        }
        dispatch(requestPatchApplicationStagedPremiumSettings(request));
    };
    const appPremiumSettingComponents = {
        flat: (
            <PremiumSettingsWizardHOC
                isChangingStatusOfApp={isChangingStatusOfApp}
                showTable={showTable}
                isEditMode={isEditMode}
                isApplicationPremium={isApplicationPremium}
                applicationPremiumCancelHandler={applicationPremiumCancelHandler}
                selectedApplicationCaseTypeId={selectedApplicationCaseTypeId}
                onApplicationPremiumEdit={onApplicationPremiumEdit}
                applicationPremiumSaveButtonLabel={applicationPremiumSaveButtonLabel}
                loggedInUser={loggedInUser}
                onCancel={onCancel}
            />
        ),
        staged: ( 
            <ApplicationStagedPremiumSettings
                applicationPolicy={applicationPolicy}
                stagedPremiumSettingsWizard={stagedPremiumsWizard}
                appStagedSubSettings={appStagedSubSettings}
                stagedPremiums={stagedPremiumSettings}
                onEditStaged={onEditStaged}
                isChangingStatusOfApp={isChangingStatusOfApp}
                onCancel={onCancel}
                goingLiveLoading={goingLiveLoading}
                handleSubmit={handleStagedSubmit}
                loggedInUser={loggedInUser}
                applicationPremiumSettingsRevisions={applicationPremiumSettingsRevisions}
                openModal={openRevisionsModal}
                setOpenModal={setOpenRevisionsModal}
                selectedPreviousUpdate={selectedPreviousUpdate}
                setSelectedPreviousUpdate={setSelectedPreviousUpdate}
                appStagedEmphasizedFields={appStagedEmphasizedFields}
                appStagedHighlightedFields={appStagedHighlightedFields}
            />
        ),
        stagedEdit: ( 
            <StagedPremiumSettingsWizardHOC
                onCancel={onCancel}
                isApplicationPremium={true}
                isEdit={true}
                handleSubmit={handleStagedSubmit}
            />
        ),
        loader : ( 
            <Loader />
        ),
        none: <></>
    };
    const configureOutput = (flatPremiumSettingsExist && !stagedPremiumSettingsExist)
        ? FLAT_PREMIUM_SETTINGS 
        : (stagedPremiumSettingsExist && isCanadaCountry && isApplicationPremium && !isEditStaged)
            ? STAGED_PREMIUM_SETTINGS
            : (stagedPremiumSettingsExist && isCanadaCountry && isApplicationPremium && isEditStaged)
                ? STAGED_PREMIUM_EDIT
                : (!flatPremiumSettingsExist && !stagedPremiumSettingsExist)
                    ? LOADER
                    : NONE;
                    
    return appPremiumSettingComponents[configureOutput];
}

export default ApplicationPremiumSettingsHOC;